const evaluateRule = ({ ruleData, objectToEvaluate, defaultResult }) => {
  // Handle actual rule criteria
  const { field, operator, value } = ruleData;

  const fieldValue = objectToEvaluate[field];
  if (!fieldValue) return defaultResult;

  let match = false;
  if (typeof value === 'string') {
    switch (operator) {
      case 'equal':
        match = fieldValue === value;
        break;
      case 'not':
        match = fieldValue !== value;
        break;
      case 'starts_with':
        match = typeof fieldValue === 'string' && fieldValue.startsWith(value);
        break;
      case 'ends_with':
        match = typeof fieldValue === 'string' && fieldValue.endsWith(value);
        break;
      case 'contains':
        match = typeof fieldValue === 'string' && fieldValue.includes(value);
        break;
      case 'not_contains':
        match = typeof fieldValue === 'string' && !fieldValue.includes(value);
        break;
      case 'match':
        try {
          const regex = new RegExp(value);
          match = typeof fieldValue === 'string' && regex.test(fieldValue);
        } catch (err) {
          // Their loss. Invalid regex, do not match anything
        }
    }
  } else if (typeof value === 'number') {
    switch (operator) {
      case 'equal':
        match = typeof fieldValue === 'number' && fieldValue === value;
        break;
      case 'lower_than':
        match = typeof fieldValue === 'number' && fieldValue < value;
        break;
      case 'lower_than_or_equal':
        match = typeof fieldValue === 'number' && fieldValue <= value;
        break;
      case 'greater_than_or_equal':
        match = typeof fieldValue === 'number' && fieldValue >= value;
        break;
      case 'greater_than':
        match = typeof fieldValue === 'number' && fieldValue > value;
        break;
    }
  } else if (Array.isArray(value)) {
    switch (operator) {
      case 'in':
        match = Array.isArray(fieldValue) ? fieldValue.some(v => value.includes(v)) : value.includes(fieldValue);
        break;
      case 'not_in':
        match = Array.isArray(fieldValue) ? !fieldValue.some(v => value.includes(v)) : !value.includes(fieldValue);
        break;
      case 'contains_all':
        match = Array.isArray(fieldValue) && value.every((val) => fieldValue.includes(val));
        break;
      case 'contains_none':
        match = Array.isArray(fieldValue) && value.every((val) => !fieldValue.includes(val));
        break;
      case 'between':
        if (value.length === 2) {
          const [min, max] = value.sort();
          match = typeof fieldValue === 'number' && fieldValue > min && fieldValue < max;
        }
        break;
    }
  }
  return {
    isMatch: match,
    score: match ? 1 : 0, // Individual rule contributes 1 to the score if it matches, 0 otherwise
  };
};

const evaluateRuleGroup = ({ ruleData, presentationContext }) => {
  // Default result for unmatched or unhandled cases
  const defaultResult = { isMatch: false, score: 0 };

  if ('field' in ruleData) {
    // Evaluate a single rule which was found within a group
    return evaluateRule({ ruleData, objectToEvaluate: presentationContext, defaultResult });
  }

  if ('rules' in ruleData) {
    // Handle rule groups (AND/OR/NOT)
    const { type, rules } = ruleData;

    if (!rules || rules.length === 0) {
      return defaultResult;
    }

    // Recursively evaluate all sub-rules and store their results
    const results = rules.map((subRule) =>
      evaluateRule({ ruleData: subRule, objectToEvaluate: presentationContext, defaultResult }),
    );

    // Calculate the total score from all successful sub-rules
    const totalScore = results
      .filter(res => res.isMatch) // Only count matches
      .reduce((sum, res) => sum + res.score, 0);

    let groupMatch = false;

    switch (type) {
      case 'and':
        // AND requires ALL sub-rules to match.
        // The score will only be the sum of all sub-rule scores if all sub-rules match.
        const allMatch = results.every(res => res.isMatch);
        groupMatch = allMatch;
        return {
          isMatch: groupMatch,
          score: groupMatch ? totalScore : 0,
        };
      case 'or':
        // OR requires AT LEAST ONE sub-rule to match.
        // Find the maximum score among all matching sub-rules/groups.
        // We only consider the scores of sub-rules that actually matched.
        const matchingResults = results.filter(res => res.isMatch);
        groupMatch = matchingResults.length > 0;

        const maxScore = groupMatch
          ? matchingResults.reduce((max, res) => Math.max(max, res.score), 0)
          : 0;

        return {
          isMatch: groupMatch,
          score: maxScore, // Use the score of the best matching sub-rule/group
        };
      case 'not':
        // NOT applies a negation to the result of the group (often treated as an implicit AND)
        // Original logic: `!rules.every(...)`. This negates the result of an implicit 'AND'.
        // If the 'AND' is true (all sub-rules match), 'NOT' is false.
        // If the 'AND' is false (at least one sub-rule fails), 'NOT' is true.
        const notEveryMatch = !results.every(res => res.isMatch);
        groupMatch = notEveryMatch;
        // The score for 'NOT' is tricky. A common convention is:
        // - If NOT is true, the score is 1 (for the NOT condition itself).
        // - If NOT is false, the score is 0.
        // It does not typically inherit the sub-scores because the condition is about a failure.
        return {
          isMatch: groupMatch,
          score: groupMatch ? 1 : 0,
        };
      default:
        return defaultResult;
    }
  }

  return defaultResult;
};

export {
  evaluateRuleGroup,
};
